import React from "react";
import { ControlLabel, FormControl } from "react-bootstrap";
import { getMonth, makeUniqueKey, formatSessionDateLabel, sortProgramSessions } from "../helper";

function formatDate(dateString) {
  if (!dateString) {
    return "";
  }

  const date = new Date(dateString);
  return `${getMonth(date.getMonth() + 1)} ${date.getDate()}, ${date.getFullYear()}`;
}

function formatScheduleDate(dateString) {
  if (!dateString) {
    return "";
  }

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function parseDate(dateString) {
  if (!dateString) {
    return null;
  }

  const parsed = new Date(dateString);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function buildMiniCalendar(startDateString, endDateString) {
  const startDate = parseDate(startDateString);
  const endDate = parseDate(endDateString);

  if (!startDate || !endDate) {
    return null;
  }

  const timelineStart = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
  const timelineEnd = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0, 23, 59, 59, 999);
  const totalDays = Math.max(1, Math.round((timelineEnd - timelineStart) / 86400000) + 1);
  const startOffsetDays = Math.max(0, Math.round((startDate - timelineStart) / 86400000));
  const activeDays = Math.max(1, Math.round((endDate - startDate) / 86400000) + 1);
  const monthSpan = Math.max(1, (endDate.getFullYear() - startDate.getFullYear()) * 12 + (endDate.getMonth() - startDate.getMonth()) + 1);
  const months = [];

  for (let monthIndex = timelineStart.getMonth(), year = timelineStart.getFullYear(); ; ) {
    const monthDate = new Date(year, monthIndex, 1);
    months.push({
      label: getMonth(monthDate.getMonth() + 1),
    });

    if (monthDate.getFullYear() === timelineEnd.getFullYear() && monthDate.getMonth() === timelineEnd.getMonth()) {
      break;
    }

    monthIndex += 1;
    if (monthIndex > 11) {
      monthIndex = 0;
      year += 1;
    }
  }

  return {
    months,
    monthSpan,
    startOffset: (startOffsetDays / totalDays) * 100,
    activeWidth: (activeDays / totalDays) * 100,
  };
}

function buildStatusTone(status) {
  const normalized = (status || "").toLowerCase();

  if (normalized.includes("waitlist")) {
    return "program-status program-status-warn";
  }

  if (normalized === "open" || normalized === "live") {
    return "program-status program-status-good";
  }

  if (normalized === "closed" || normalized === "expired") {
    return "program-status program-status-muted";
  }

  return "program-status program-status-neutral";
}

function ProgramDetails(props) {
  const {
    selected_program,
    team_name,
    division,
    status,
    VTsessions,
    plans,
    selected_plan,
    planSelectHandler,
    invalidFieldIds = [],
    disabled,
    showForm,
    continueSpinner,
    onContinue,
    EF1,
    hideNotes = false,
  } = props;

  const sessions = selected_program?.sessions || [];
  const virtualSessions = selected_program?.VTsessions || VTsessions || [];
  const aboutText = hideNotes && selected_program?.additional_notes
    ? [selected_program?.program_description, selected_program.additional_notes].filter(Boolean).join("\n\n")
    : selected_program?.program_description;
  const programDatesRef = React.useRef(null);
  const [programDatesHeight, setProgramDatesHeight] = React.useState(null);

  React.useEffect(() => {
    if (!hideNotes || !programDatesRef.current) {
      return undefined;
    }

    const updateHeight = () => {
      if (programDatesRef.current) {
        setProgramDatesHeight(programDatesRef.current.offsetHeight);
      }
    };

    updateHeight();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(updateHeight);
      observer.observe(programDatesRef.current);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, [hideNotes, aboutText, selected_program?.start_date, selected_program?.end_date, selected_program?.registration_end_date]);

  if (EF1) {
    return (
      <section className="program-details-shell program-details-shell-ef1">
        <div className="program-details-card">
          <div className="program-details-hero">
            <div className="program-details-kicker">Program Preview</div>
            <h3 className="program-details-title">Sign up to Hoopstir</h3>
            <p className="program-details-copy">
              Start saving time, effort, and money with Hoopstir.
            </p>
          </div>
          <div className="program-details-grid">
            <div className="program-details-point">Dashboard analytics</div>
            <div className="program-details-point">Enrolment forms</div>
            <div className="program-details-point">Custom fields</div>
            <div className="program-details-point">Team store</div>
            <div className="program-details-point">Waitlists</div>
            <div className="program-details-point">Integrated payments</div>
          </div>
        </div>
      </section>
    );
  }

  const subtitleParts = [];
  const hasError = (id) => invalidFieldIds.includes(id);
  const miniCalendar = selected_program
    ? buildMiniCalendar(selected_program.start_date, selected_program.end_date)
    : null;

  if (selected_program?.ages) {
    subtitleParts.push(selected_program.ages);
  }
  if (division) {
    subtitleParts.push(division);
  }
  if (team_name) {
    subtitleParts.push(`[${team_name}]`);
  }

  return (
    <section className="program-details-shell">
      <div className="program-details-card">
        <div className="program-details-hero">
          <div className="program-details-kicker">Program Preview</div>
          <div className="program-details-heading-row">
            <h3 className="program-details-title">
              {selected_program ? selected_program.group_name : "Register Today!"}
            </h3>
            {status ? (
              <span className={buildStatusTone(status)}>
                {status.replace("WAITLIST OPEN", "Waitlist Open")}
              </span>
            ) : null}
          </div>
          {subtitleParts.length > 0 ? (
            <div className="program-details-subtitle">
              {subtitleParts.join(" · ")}
            </div>
          ) : null}
        </div>

        <div className="program-details-summary">
          {selected_program?.start_date && selected_program?.end_date ? (
            <div ref={programDatesRef} className="program-detail-chip program-detail-chip-stack">
              <div className="program-detail-chip-label">Program dates</div>
              <div className="program-detail-chip-value">
                {formatDate(selected_program.start_date)} to {formatDate(selected_program.end_date)}
              </div>
              {miniCalendar ? (
                <div className="program-mini-calendar" aria-hidden="true">
                  <div className="program-mini-calendar-meta">
                    {miniCalendar.monthSpan} month{miniCalendar.monthSpan === 1 ? "" : "s"}
                  </div>
                  <div className="program-mini-calendar-track">
                    <div
                      className="program-mini-calendar-active"
                      style={{
                        width: `${miniCalendar.activeWidth}%`,
                        left: `${miniCalendar.startOffset}%`,
                      }}
                    />
                  </div>
                  <div
                    className="program-mini-calendar-months"
                    style={{ gridTemplateColumns: `repeat(${miniCalendar.months.length}, minmax(0, 1fr))` }}
                  >
                    {miniCalendar.months.map((month, index) => (
                      <div className="program-mini-calendar-month" key={index}>
                        <span>{month.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              {selected_program?.registration_end_date ? (
                <div className="program-detail-chip-subline">
                  <div className="program-detail-chip-label program-detail-chip-label-inline">
                    Registration closes
                  </div>
                  <div className="program-detail-chip-value program-detail-chip-value-inline">
                    {formatDate(selected_program.registration_end_date)}
                  </div>
                </div>
              ) : null}
              {(aboutText || (!hideNotes && selected_program?.additional_notes)) ? (
                <div className="program-details-section program-details-section-duo">
                  {aboutText ? (
                    <div className="program-details-section-panel">
                      <div className="program-details-section-label">About</div>
                      <div className="program-details-body">
                        {aboutText}
                      </div>
                    </div>
                  ) : null}

                  {!hideNotes && selected_program?.additional_notes ? (
                    <div className="program-details-section-panel">
                      <div className="program-details-section-label">Notes</div>
                      <div className="program-details-body">
                        {selected_program.additional_notes}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}

          {selected_program?.registration_end_date ? (
            <div
              className={`program-detail-chip program-detail-chip-split ${hideNotes ? "program-detail-chip-split-ef3" : ""}`}
              style={hideNotes && programDatesHeight ? { height: `${programDatesHeight}px` } : undefined}
            >
              {sessions.length > 0 ? (
                  <div className="program-detail-schedule-panel">
                  <div className="program-detail-chip-label">
                    Schedule
                  </div>
                  <div className="program-session-list program-session-list-inline">
                    {sortProgramSessions(sessions).map((session) => (
                      <div className="program-session-item program-session-item-inline" key={makeUniqueKey()}>
                        <div className="program-session-pill">{formatScheduleDate(session.date)}</div>
                          <div className="program-session-main">
                          <div className="program-session-title-row">
                            <div className="program-session-title">{session.type}</div>
                            {session.location ? (
                              <div className="program-session-location-inline">
                                <span className="program-session-meta-separator"> | </span>
                                <span className="program-session-meta-label">Location</span>{" "}
                                {session.location}
                              </div>
                            ) : null}
                          </div>
                          <div className="program-session-location-inline">
                            <span className="program-session-meta-separator"> | </span>
                            <span className="program-session-meta-label">Time</span>{" "}
                            {session.start_time} - {session.end_time}
                          </div>
                          <div className="program-session-location-inline">
                            <span className="program-session-meta-separator"> | </span>
                            <span className="program-session-meta-label">Date</span>{" "}
                            {formatScheduleDate(session.date)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              {selected_program?.plans?.length > 0 ? (
                <div className={`program-detail-plan-picker program-detail-plan-picker-below ${hasError("selected_plan") ? "enrolment-invalid-field" : ""}`}>
                    <ControlLabel className={`program-detail-plan-label ${hasError("selected_plan") ? "enrolment-invalid-label" : ""}`}>
                      Price Plan
                    </ControlLabel>
                    <FormControl
                      className="opaque-grey program-detail-plan-select"
                      id="selected_plan"
                      onChange={planSelectHandler}
                      componentClass="select"
                      disabled={disabled}
                      value={selected_plan || ""}
                    >
                      <option value="" disabled>
                        select...
                      </option>
                      {plans}
                    </FormControl>
                  </div>
                ) : null}
            </div>
          ) : null}
        </div>

        {virtualSessions.length > 0 ? (
          <div className="program-details-section">
            <div className="program-details-section-label">Virtual Training</div>
            <div className="program-session-list">
              {virtualSessions.map((session) => (
                <div className="program-session-item" key={makeUniqueKey()}>
                  <div className="program-session-pill program-session-pill-virtual">
                    {formatScheduleDate(session.date)}
                  </div>
                  <div className="program-session-main">
                    <div className="program-session-title">{session.name}</div>
                    <div className="program-session-location-inline">
                      <span className="program-session-meta-separator"> | </span>
                      <span className="program-session-meta-label">Time</span>{" "}
                      {session.start_time} - {session.end_time}
                    </div>
                    <div className="program-session-location-inline">
                      <span className="program-session-meta-separator"> | </span>
                      <span className="program-session-meta-label">Date</span>{" "}
                      {formatScheduleDate(session.date)}
                      {session.coach ? ` · ${session.coach}` : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {selected_program ? (
          <div className="program-details-footer">
            <div className="program-details-footer-copy">
              Review the program details, then continue to open the registration form.
            </div>
            {!showForm ? (
              <div className="program-details-footer-action">
                {continueSpinner ? (
                  <div className="enrolment-loader"></div>
                ) : (
                  <button
                    type="button"
                    onClick={onContinue}
                    disabled={disabled || !selected_plan}
                    className="button2 btn btn-default continue-button"
                  >
                    Continue
                  </button>
                )}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}

export default ProgramDetails;
